import { EntryEditError } from '../edits.ts';
import { javaStrip } from '../javaText.ts';
import { int, scalar, text, transaction, type SqlConnection } from '../sql.ts';
import type { ChronologyDate } from '../calendar/chronologyDate.ts';
import { STORY_LIMITS, cleanStoryName, wordCount } from './story.ts';

/**
 * Writing a scene (docs/PARITY-STEP-5-PLAN.md section 4), as the desktop's
 * `StoryRepository#saveScene` and `StoryService` do:
 * - a save writes the scene's title, manuscript, story beats, date and links; when the
 *   manuscript changed, the saved text is kept as a revision, and only the newest
 *   `keepRevisions` (Settings > Writing, 20 unless changed) are kept;
 * - restoring a revision saves its text as the scene's, which is itself kept as a revision;
 * - each focus-mode sitting goes into the project's writing log (`writing_sessions`),
 *   unless it was under a minute with no change in words (`recordWritingSession`).
 */

/** What a save changes; what's left out stays as it is. */
export interface SceneChanges {
  readonly title?: string;
  readonly contentHtml?: string;
  readonly storyBeats?: string;
  /** Null for no date. */
  readonly timelinePosition?: ChronologyDate | null;
  readonly entryIds?: readonly string[];
  readonly eventIds?: readonly string[];
  readonly wordGoal?: number;
}

export interface SceneRevision {
  readonly id: string;
  readonly sceneId: string;
  readonly wordCount: number;
  readonly created: string;
}

export interface WritingSession {
  readonly id: string;
  readonly projectId: string;
  /** Null once the scene is deleted. */
  readonly sceneId: string | null;
  /** The title at the time, so the log still reads well after a rename or deletion. */
  readonly sceneTitle: string;
  readonly started: string;
  readonly ended: string;
  readonly activeSeconds: number;
  readonly wordsStart: number;
  readonly wordsEnd: number;
}

export type WritingSessionInput = Omit<WritingSession, 'id'>;

/** `Settings.SCENE_REVISIONS`: 20, from 1 to 200. */
export const SCENE_REVISIONS_DEFAULT = 20;
export const SCENE_REVISIONS_MAX = 200;
export const STORY_BEATS_MAX = 20_000;
/** A manuscript the size of a long novel; a scene is far less. */
export const SCENE_HTML_MAX = 5_000_000;
/** Sittings shorter than this, with no words written, aren't worth a line in the log. */
export const MIN_LOGGED_SECONDS = 60;
const DATE_LABEL_MAX = 200;
const MAX_LONG = 9223372036854775807n;

/** `revisionId` names the revision a changed text makes (fixed only in tests). */
export function updateScene(
  conn: SqlConnection,
  id: string,
  changes: SceneChanges,
  keepRevisions: number = SCENE_REVISIONS_DEFAULT,
  now: string = new Date().toISOString(),
  revisionId: string = crypto.randomUUID(),
): void {
  if (!Number.isInteger(keepRevisions) || keepRevisions < 1 || keepRevisions > SCENE_REVISIONS_MAX) {
    throw new EntryEditError('invalid', `Between 1 and ${SCENE_REVISIONS_MAX} earlier versions can be kept.`);
  }
  transaction(conn, () => {
    const previous = conn.all('SELECT content_html FROM story_scenes WHERE id = ?', [id])[0];
    if (!previous) throw new EntryEditError('not_found', 'That scene no longer exists.');
    const set: string[] = [];
    const params: (string | number | bigint)[] = [];
    const column = (name: string, value: string | number | bigint) => {
      set.push(`${name} = ?`);
      params.push(value);
    };
    if (changes.title !== undefined) column('title', cleanStoryName(changes.title, 'Untitled Scene'));
    if (changes.contentHtml !== undefined) {
      if (changes.contentHtml.length > SCENE_HTML_MAX) throw new EntryEditError('invalid', "The scene's text is too long to save.");
      column('content_html', changes.contentHtml);
    }
    if (changes.storyBeats !== undefined) {
      if (changes.storyBeats.length > STORY_BEATS_MAX) throw new EntryEditError('invalid', `Story beats can be at most ${STORY_BEATS_MAX.toLocaleString('en')} characters.`);
      column('story_beats', changes.storyBeats);
    }
    if (changes.timelinePosition !== undefined) {
      const date = changes.timelinePosition === null ? null : cleanDate(changes.timelinePosition);
      column('has_timeline', date ? 1 : 0);
      column('timeline_sort_key', date ? date.sortKey : 0n);
      column('timeline_label', date ? date.label : '');
      column('timeline_uncertain', date?.uncertain ? 1 : 0);
    }
    if (changes.wordGoal !== undefined) {
      if (!Number.isInteger(changes.wordGoal) || changes.wordGoal > STORY_LIMITS.goal) throw new EntryEditError('invalid', 'A word goal must be a whole number of words.');
      column('word_goal', Math.max(0, changes.wordGoal));
    }
    column('modified', now);
    conn.run(`UPDATE story_scenes SET ${set.join(', ')} WHERE id = ?`, [...params, id]);

    if (changes.entryIds !== undefined) {
      const ids = [...new Set(changes.entryIds)];
      for (const entryId of ids) mustExist(conn, 'lore_entries', entryId, 'One of the linked entries no longer exists.');
      conn.run('DELETE FROM story_scene_entries WHERE scene_id = ?', [id]);
      for (const entryId of ids) conn.run('INSERT INTO story_scene_entries (scene_id, entry_id) VALUES (?, ?)', [id, entryId]);
    }
    if (changes.eventIds !== undefined) {
      const ids = [...new Set(changes.eventIds)];
      for (const eventId of ids) mustExist(conn, 'chronology_events', eventId, 'One of the linked events no longer exists.');
      conn.run('DELETE FROM story_scene_events WHERE scene_id = ?', [id]);
      for (const eventId of ids) conn.run('INSERT INTO story_scene_events (scene_id, event_id) VALUES (?, ?)', [id, eventId]);
    }

    // As saveScene: the new text is kept as a revision when it differs from what was stored.
    if (changes.contentHtml !== undefined && changes.contentHtml !== text(previous['content_html'])) {
      conn.run('INSERT INTO story_scene_revisions (id, scene_id, content_html, word_count, created) VALUES (?, ?, ?, ?, ?)', [
        revisionId,
        id,
        changes.contentHtml,
        wordCount(changes.contentHtml),
        now,
      ]);
      conn.run(
        `DELETE FROM story_scene_revisions WHERE scene_id = ? AND id NOT IN (
           SELECT id FROM story_scene_revisions WHERE scene_id = ? ORDER BY created DESC, rowid DESC LIMIT ?)`,
        [id, id, keepRevisions],
      );
    }
  });
}

/** The scene's revisions, newest first, without their text. */
export function listRevisions(conn: SqlConnection, sceneId: string): SceneRevision[] {
  return conn
    .all('SELECT id, scene_id, word_count, created FROM story_scene_revisions WHERE scene_id = ? ORDER BY created DESC, rowid DESC', [sceneId])
    .map((row) => ({ id: text(row['id']), sceneId: text(row['scene_id']), wordCount: int(row['word_count']), created: text(row['created']) }));
}

/** One revision's text, or null when it's gone. */
export function revisionHtml(conn: SqlConnection, revisionId: string): string | null {
  const value = scalar(conn, 'SELECT content_html FROM story_scene_revisions WHERE id = ?', [revisionId]);
  return value === null ? null : text(value);
}

/** Puts a revision's text back as the scene's (`restoreRevision`); that save is kept as a revision too. */
export function restoreRevision(
  conn: SqlConnection,
  sceneId: string,
  revisionId: string,
  keepRevisions: number = SCENE_REVISIONS_DEFAULT,
  now: string = new Date().toISOString(),
  newRevisionId: string = crypto.randomUUID(),
): void {
  const html = scalar(conn, 'SELECT content_html FROM story_scene_revisions WHERE id = ? AND scene_id = ?', [revisionId, sceneId]);
  if (html === null) throw new EntryEditError('not_found', 'That earlier version is no longer kept.');
  updateScene(conn, sceneId, { contentHtml: text(html) }, keepRevisions, now, newRevisionId);
}

/**
 * Records a focus-mode sitting in its project's writing log, unless it was too brief to
 * matter: under a minute of writing and no change in words. Returns whether it was recorded.
 */
export function recordWritingSession(conn: SqlConnection, session: WritingSessionInput, id: string = crypto.randomUUID()): boolean {
  const { activeSeconds, wordsStart, wordsEnd } = session;
  for (const n of [activeSeconds, wordsStart, wordsEnd]) {
    if (!Number.isSafeInteger(n) || n < 0) throw new EntryEditError('invalid', 'A sitting is counted in whole seconds and words.');
  }
  if (Number.isNaN(Date.parse(session.started)) || Number.isNaN(Date.parse(session.ended))) throw new EntryEditError('invalid', "A sitting's times must be dates.");
  if (activeSeconds < MIN_LOGGED_SECONDS && wordsEnd === wordsStart) return false;
  transaction(conn, () => {
    mustExist(conn, 'story_projects', session.projectId, 'That story project no longer exists.');
    // A scene deleted meanwhile leaves the sitting in the log, under its title.
    const sceneId = session.sceneId !== null && int(scalar(conn, 'SELECT COUNT(*) FROM story_scenes WHERE id = ?', [session.sceneId])) > 0 ? session.sceneId : null;
    conn.run(
      `INSERT INTO writing_sessions (id, project_id, scene_id, scene_title, started, ended, active_seconds, words_start, words_end)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [id, session.projectId, sceneId, cleanStoryName(session.sceneTitle, ''), toInstant(session.started), toInstant(session.ended), activeSeconds, wordsStart, wordsEnd],
    );
  });
  return true;
}

/** The project's writing log, most recent first. */
export function listWritingSessions(conn: SqlConnection, projectId: string): WritingSession[] {
  return conn
    .all('SELECT id, project_id, scene_id, scene_title, started, ended, active_seconds, words_start, words_end FROM writing_sessions WHERE project_id = ? ORDER BY started DESC', [projectId])
    .map((row) => ({
      id: text(row['id']),
      projectId: text(row['project_id']),
      sceneId: row['scene_id'] === null || row['scene_id'] === undefined ? null : text(row['scene_id']),
      sceneTitle: text(row['scene_title']),
      started: text(row['started']),
      ended: text(row['ended']),
      activeSeconds: int(row['active_seconds']),
      wordsStart: int(row['words_start']),
      wordsEnd: int(row['words_end']),
    }));
}

// ---- the writing timer ----

/**
 * Focus mode's writing timer (`WritingClock`): counts time from when it starts, but stops
 * once nothing has been typed for `idleMs`, and picks up again at the next key. Driven by
 * its owner (`tick` each second, `activity` on each key), with the time passed in.
 */
export class WritingClock {
  readonly #idleMs: number;
  #lastTick: number;
  #lastActivity: number;
  #activeMs = 0;

  constructor(idleMs: number, nowMs: number) {
    this.#idleMs = idleMs;
    this.#lastTick = nowMs;
    this.#lastActivity = nowMs;
  }

  /** Something was typed. */
  activity(nowMs: number): void {
    this.tick(nowMs);
    this.#lastActivity = nowMs;
  }

  /** Adds the time since the last tick, as much of it as falls before going idle. */
  tick(nowMs: number): void {
    if (nowMs <= this.#lastTick) return;
    const countUntil = Math.min(nowMs, this.#lastActivity + this.#idleMs);
    if (countUntil > this.#lastTick) this.#activeMs += countUntil - this.#lastTick;
    this.#lastTick = nowMs;
  }

  isIdle(nowMs: number): boolean {
    return nowMs - this.#lastActivity >= this.#idleMs;
  }

  get activeSeconds(): number {
    return Math.floor(this.#activeMs / 1000);
  }
}

/** `0:42`, `12:05`, or `1:05:12`. */
export function formatClock(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const two = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${two(m)}:${two(s)}` : `${m}:${two(s)}`;
}

/** `42 min`, `1 h 05 min`, or `under a minute`, for the writing log. */
export function describeWritingTime(seconds: number): string {
  if (seconds < 60) return 'under a minute';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h > 0 ? `${h} h ${String(m).padStart(2, '0')} min` : `${m} min`;
}

// ---- checks ----

/** A date's label is trimmed and kept on one line, as `ChronologyDate`'s constructor does. */
function cleanDate(date: ChronologyDate): ChronologyDate {
  if (date.label.length > DATE_LABEL_MAX) throw new EntryEditError('invalid', `A date's label can be at most ${DATE_LABEL_MAX} characters.`);
  if (typeof date.sortKey !== 'bigint' || date.sortKey > MAX_LONG || date.sortKey < -MAX_LONG - 1n) throw new EntryEditError('invalid', 'That sort key is too large.');
  return { sortKey: date.sortKey, label: javaStrip(date.label.replace(/[\r\n]+/g, ' ')), uncertain: date.uncertain === true };
}

/** As `Instant.toString` reads back: an ISO time in UTC. */
function toInstant(time: string): string {
  return new Date(time).toISOString();
}

/** `table` is always one of this module's literals, never input. */
function mustExist(conn: SqlConnection, table: string, id: string, message: string): void {
  if (int(scalar(conn, `SELECT COUNT(*) FROM ${table} WHERE id = ?`, [id])) === 0) throw new EntryEditError('not_found', message);
}
